import type AbstractFilterControl from "./AbstractFilterControl";
import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { Criteria, InteractsCriteria } from "@esWebAPI/es/model/Criteria";
import type { Attribute, Feature } from "@esWebAPI/es/model/Feature";

import AbstractSpatialFilterControl from "./AbstractSpatialFilterControl";

const DIST_NAME = "ES_IN_DIST";
const TIME_NAME = "ES_IN_TIME";

export default class InteractsFilterControl extends AbstractSpatialFilterControl {
    private layerModule;
    private hasAttrs: boolean;
    private feature?: Feature | null;
    private geoShapeKey?: string;

    private name = this.getName(DIST_NAME);

    private unit: string;
    private origin: [string, number, number];
    private isEditable: boolean;

    private mapType: string;

    private uniqSuffix?: string;

    constructor(layerViewModel: LayerViewModel, origin: undefined | null, geoShapeKey: string | undefined, feature: Feature | undefined, layerModule) {
        super(layerViewModel, "Interacts"); // let attrName be undefined

        this.layerModule = layerModule;
        this.hasAttrs = false;
        this.feature = feature;
        this.geoShapeKey = geoShapeKey;

        this.name = this.getName(DIST_NAME);
    }

    init(): JQueryDeferred<AbstractFilterControl> {
        var da = this.layerViewModel.getDynamicAttributes().get("$type", "DriveTimeDistanceAttribute");
        this.unit = da ? da.unit : es.util.getDefaultDistanceUnit();
        this.hasAttrs = !!da;

        if (this.feature) {
            this.fetchMapType(this.feature.layerViewId);

            if (this.isDriveTime() || this.isRadius()) {
                const attrs = ["ES_LONG_ORIGIN", "ES_LAT_ORIGIN"];
                if (this.layerModule.getById(this.feature.layerViewId).getType() == "MapScopeRadiusLayerView") {
                    attrs.push("ES_RADIUS_META_UNIT");
                }

                return $.when(
                    es.api.feature.get(this.feature.layerViewId, this.feature.id, attrs),
                    es.api.security.isEditableLayerView(this.feature.layerViewId)
                ).then((bufferFeature, isEditable) => {
                    // retrieve the radius center
                    this.feature = bufferFeature;
                    this.origin = ["PT", bufferFeature.attrs.ES_LONG_ORIGIN, bufferFeature.attrs.ES_LAT_ORIGIN];
                    if (this.isRadius()) {
                        this.unit = bufferFeature.attrs.ES_RADIUS_META_UNIT;
                    }

                    this.isEditable = isEditable;
                    return this;
                });
            }
        }

        return es.util.deferred.resolved(this);
    }

    getTitle() {
        if (this.isDriveTime()) {
            return $i18n('Filter_to_Drive_Time');
        }

        if (this.isRadius()) {
            return $i18n('Filter_to_Radius');
        }

        return $i18n("Filter_Within_Area");
    }

    getDescription(): string {
        return this.feature ? this.feature.label : $i18n("Show_points_within_specified_area");
    }

    private fetchMapType(layerId: number) {
        const lvm = this.layerModule.getById(layerId);
        //Filter is being initialized but soft link layer just got removed.
        // Filter needs to be initialized so that it can be destroyed
        this.mapType = lvm ? lvm.getType() : "Ignore this";
    }

    toCriteria(): InteractsCriteria {
        const criteria = {
            $type: "Interacts",
            name: this.name,
            geoShapeKey: this.geoShapeKey
        };

        if (this.feature) {
            criteria.layerViewId = this.feature.layerViewId;
            criteria.featureId = this.feature.id;
        }

        return criteria;
    }

    fromCriteria(criteria: Criteria): JQueryDeferred<AbstractFilterControl> {
        if (criteria.$type !== "Interacts") {
            throw new Error("Trying to update filter with invalid criteria");
        }

        this.uniqSuffix = _.last(criteria.name.split('_'));
        this.name = criteria.name;
        this.geoShapeKey = criteria.geoShapeKey;
        this.hasAttrs = !!this.layerViewModel.getDynamicAttributes().find((da) => this.name === da.criteriaName);

        if (!criteria.featureId || !criteria.layerViewId) {
            return es.util.deferred.resolved(this);
        }

        this.fetchMapType(criteria.layerViewId);
        if (!this.isDriveTime() && !this.isRadius()) {
            return es.util.deferred.resolved(this);
        }

        const attrs = ["ES_LONG_ORIGIN", "ES_LAT_ORIGIN"];
        if (this.layerModule.getById(criteria.layerViewId).getType() === "MapScopeRadiusLayerView") {
            attrs.push("ES_RADIUS_META_UNIT");
        }

        return $.when(
            es.api.feature.get(criteria.layerViewId, criteria.featureId, attrs),
            es.api.security.isEditableLayerView(criteria.layerViewId)
        ).then((bufferFeature, isEditable) => {
            if (bufferFeature){ // user might no longer be accesible, behave as if the link was broken
                this.feature = bufferFeature;
                this.origin = ["PT", bufferFeature.attrs.ES_LONG_ORIGIN, bufferFeature.attrs.ES_LAT_ORIGIN];
                if (this.isRadius()) {
                    this.unit = bufferFeature.attrs.ES_RADIUS_META_UNIT;
                }
                this.isEditable = isEditable;
            }

            return this;
        });
    }

    private isRadius(): boolean {
        return this.mapType === "MapScopeRadiusLayerView";
    }

    private isDriveTime(): boolean {
        return this.mapType === "MapScopeDriveTimeLayerView";
    }

    getDynamicAttributes(criteria: Criteria): JQueryDeferred<Attribute[]> {
        if (this.isRadius()) {
            return es.util.deferred.resolved([this.getDefaultAttr()]);
        }

        if (!this.hasAttrs || !this.isValid()) {
            return es.util.deferred.resolved([]);
        }

        if (this.isDriveTime()) {
            return this.canAddDynamicAttributes(criteria).then((allowed) => {
                // Silently remove dynamicAttribute ... should fire an onUpdate if the value change ?
                this.setHasAttrs(allowed);
                return allowed ? [this.getDriveTimeAttr(), this.getDriveTimeDistanceAttr()] : [];
            });
        }

        return es.util.deferred.resolved([]);
    }

    private getName(prefix: string): string {
        if (!this.uniqSuffix) {
            this.uniqSuffix = this.generateUniqName();
        }

        return prefix + "_" + this.uniqSuffix;
    }

    private _getLabel(): string {
        const regex = new RegExp(/\w+ \d+ \w+ from (.+)/);
        const match = regex.exec(this.feature.label);

        if (match && match[1]) {
            return match[1];
        }

        return this.feature.label;
    }

    private getDriveTimeAttr(): Attribute {
        return {
            $type: "DriveTimeAttribute",
            name: this.getName(TIME_NAME),
            title: `Drive Time (${$i18n("Mins")}) from ${this._getLabel()}`,
            criteriaName: this.name,
            geometry: this.origin,
            type: "INTEGER"
        };
    }

    private getDefaultAttr(): Attribute {
        return {
            $type: "CalculateDistanceAttribute",
            name: this.name,
            title: `Distance (${$i18n("_Distance_Unit_" + this.unit)}) from ${this._getLabel()}`,
            criteriaName: this.name,
            geometry: this.origin,
            type: "DECIMAL",
            unit: this.unit
        };
    }

    private getDriveTimeDistanceAttr(): Attribute {
        return {
            $type: "DriveTimeDistanceAttribute",
            name: this.getName(DIST_NAME),
            title: `Distance (${$i18n("_Distance_Unit_" + this.unit)}) from ${this._getLabel()}`,
            criteriaName: this.name,
            geometry: this.origin,
            type: "DECIMAL",
            unit: this.unit
        };
    }

    getSoftLink(): {layerViewId: number, featureId: number} | null {
        if (!this.feature) {
            return null;
        }

        return {
            layerViewId: this.feature.layerViewId,
            featureId: this.feature.id
        };
    }

    lock() {
        if (this.isLocked()) {
            es.log.warn("Trying to lock already locked filter");
        }

        if (this.hasExpensiveAttributes()) {
            throw new Error($i18n('_Remove_Drive_Time_Attributes'));
        }

        this.feature = null;

        super.lock();
    }

    getSubType() {
        if (this.feature && this.feature.attrs.DISTANCE) {
            return "Filter_to_Radius";
        }

        if (this.feature && this.feature.attrs.TIME) {
            return "Filter_to_Drive_Time";
        }

        return "Filter_Within_Area";
    }

    private setHasAttrs(show: boolean) {
        if (this.hasAttrs == show) {
            return;
        }

        this.hasAttrs = show;
        this.eventChannel.fireEvent("onUpdate");
    }

    checkRemove(control: AbstractFilterControl): string | null {
        return this.hasExpensiveAttributes() && control !== this ? $i18n('_Remove_Drive_Time_Attributes') : null;
    }

    checkLock(): string | null {
        return this.hasExpensiveAttributes() && !this.isLocked() ? $i18n('_Remove_Drive_Time_Attributes') : null;
    }

    private hasExpensiveAttributes(): boolean {
        return this.isDriveTime() && this.hasAttrs;
    }

    updateBuffer(newFeatureAttrs, newGeoShapeKey: string, label: string) {
        this.geoShapeKey = newGeoShapeKey;

        if (this.isRadius()) {
            this.unit = newFeatureAttrs.ES_RADIUS_META_UNIT;
        }

        this.feature.label = label;

        this.eventChannel.fireEvent("onUpdate");
    }

    addDynamicAttributes(): JQueryDeferred<void> {
        if (this.hasAttrs) {
            es.log.error("Trying to add already existing dynamic attributes");
        }

        return this.canAddDynamicAttributes().then((allowed) => {
            this.hasAttrs = allowed;

            if (!allowed) {
                return es.util.deferred.rejected(true);
            }

            this.eventChannel.fireEvent("onUpdate");
        });
    }

    removeDynamicAttributes(): JQueryDeferred<void> {
        this.hasAttrs = false;

        this.eventChannel.fireEvent("onUpdate");

        return es.util.deferred.resolved();
    }

    hasDynamicAttributes(): boolean {
        return this.hasAttrs;
    }

    hasOptionalDynamicAttribute(): boolean {
        return this.isDriveTime() && this.layerViewModel.getGeomType() === "POINT";
    }

    setUnit(unit: string) {
        this.unit = unit;

        this.eventChannel.fireEvent("onUpdate");
    }

    getBufferType(): string | undefined {
        if (this.isDriveTime()) {
            return "drive-time";
        } else if (this.isRadius()) {
            return "radius";
        }
    }

    getEditLabel(): string | undefined {
        if (!this.feature || !this.isEditable) {
            return;
        }

        return this.isDriveTime() ? $i18n("Edit_Drive_Time") : $i18n("Edit_Radius");
    }

    private canAddDynamicAttributes(criteria?: Criteria): JQueryDeferred<boolean> {
        // if no criteria provided, just use the one on the layerView
        criteria = criteria || this.layerViewModel.getCriteriaObjRef();
        return es.api.feature.count([this.layerViewModel.getId()], criteria).then((counts) => {
            return counts[0] <= es.spatialLimits.maxFeature;
        });
    }

    static supportCriteria(criteria: Criteria): boolean {
        return criteria.$type === "Interacts";
    }

    static supportAttribute(): boolean {
        return false;
    }

    static fromCriteria(layerViewModel: LayerViewModel, criteria: Criteria, attribute: Attribute, range, layerModule): JQueryDeferred<AbstractFilterControl> {
        const filter = new InteractsFilterControl(layerViewModel, undefined, undefined, undefined, layerModule);

        return filter.init().then((filter: InteractsFilterControl) => {
            return filter.fromCriteria(criteria);
        }).fail(es.error.handleJSError);
    }

    //es.control.InteractsFilter = InteractsFilter;
}
